import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { SubUrlsEnum } from '../enums/sub-urls.enum';
import { IAvailableEventsDTOIn, IMonthlyEventData } from '../interfaces/event.interface';
import {
    IActivateMealsDTOOut,
    IBookedMealsDTOIn,
    IBookedMealsToCancel,
    IBookMealsDTOOut,
    IMealMenuFiles,
    IMealSetting,
    IMealsSettingsDTOOut,
    ISchoolMealsSettings,
    ITotalOfBookedMealsPerMonth,
    ITotalOfBookedMealsPerMonthDTOIn,
} from '../interfaces/meal.interface';
import { formatDateYYYYMMDD } from '../utils/date.utils';
import { PaymentStatusEnum } from './../enums/payment-status.enum';
import { MappersService } from './mappers.service';

@Injectable({
    providedIn: 'root',
})
export class MealsService {
    constructor(private http: HttpClient, private mapperService: MappersService) {}

    // MEALS SETTINGS
    public getSchoolMealsSettings(schoolId: number): Observable<ISchoolMealsSettings> {
        const url: string = environment.wasoApi + SubUrlsEnum.SCHOOLS + '/' + schoolId + '/';
        return this.http.get<ISchoolMealsSettings>(url + SubUrlsEnum.MEAL_SETTINGS);
    }

    public patchSchoolMealSetting(mealSetting: IMealsSettingsDTOOut): Observable<IMealSetting> {
        const url: string = environment.wasoApi + SubUrlsEnum.MEAL_SETTINGS + '/' + mealSetting.meal_setting.id;
        return this.http.put<IMealSetting>(url, mealSetting);
    }

    // AVAILABLE MEALS
    public getAllAvailableMeals(schoolId: number, selectedStudentId?: number): Observable<IMonthlyEventData[]> {
        let params: HttpParams = new HttpParams();
        if (selectedStudentId) {
            params = params.set('viewing_student_id', selectedStudentId);
        }
        return this.http
            .get<IAvailableEventsDTOIn>(environment.wasoApi + SubUrlsEnum.AVAILABLE_MEALS.replace('{{schoolId}}', schoolId.toString()), {
                params: selectedStudentId ? params : null,
            })
            .pipe(map((availableMeals: IAvailableEventsDTOIn) => this.mapperService.fromAvailableEventsToEventsData(availableMeals)));
    }

    // ACTIVATE MEALS
    public activateMeals(mealsToActivate: IActivateMealsDTOOut, schoolId: number): Observable<IAvailableEventsDTOIn> {
        return this.http.post<IAvailableEventsDTOIn>(
            environment.wasoApi + SubUrlsEnum.ACTIVATE_MEALS.replace('{{schoolId}}', schoolId.toString()),
            mealsToActivate
        );
    }

    // DISABLE MEALS
    public disableMeals(mealsToDisable: IActivateMealsDTOOut, schoolId: number): Observable<void> {
        const options: { headers: HttpHeaders; body: any } = {
            headers: new HttpHeaders(),
            body: mealsToDisable,
        };
        return this.http.delete<void>(
            environment.wasoApi + SubUrlsEnum.DISABLE_MEALS.replace('{{schoolId}}', schoolId.toString()),
            options
        );
    }

    // DISABLE FULL MONTH
    public disableFullMonthMeals(monthDate: Date, schoolId: number): Observable<void> {
        const params: HttpParams = new HttpParams().set('date', formatDateYYYYMMDD(monthDate));
        return this.http.delete<void>(environment.wasoApi + SubUrlsEnum.DISABLE_MONTH_MEALS.replace('{{schoolId}}', schoolId.toString()), {
            params,
        });
    }

    // BOOK MEALS
    public bookMeals(mealsToBook: IBookMealsDTOOut, selectedStudentId?: number): Observable<IBookedMealsDTOIn> {
        let params: HttpParams = new HttpParams();
        if (selectedStudentId) {
            params = params.set('viewing_student_id', selectedStudentId);
        }
        return this.http.post<IBookedMealsDTOIn>(environment.wasoApi + SubUrlsEnum.BOOK_MEALS, mealsToBook, {
            params: selectedStudentId ? params : null,
        });
    }

    public getTotalOfBookedMealsPerMonth(selectedStudentId?: number): Observable<ITotalOfBookedMealsPerMonth[]> {
        let params: HttpParams = new HttpParams();
        if (selectedStudentId) {
            params = params.set('viewing_student_id', selectedStudentId);
        }
        return this.http
            .get<ITotalOfBookedMealsPerMonthDTOIn>(environment.wasoApi + SubUrlsEnum.BOOKED_MEALS_BY_MONTH, {
                params: selectedStudentId ? params : null,
            })
            .pipe(map((result: ITotalOfBookedMealsPerMonthDTOIn) => result.book_meals));
    }

    public getBookedMeals(payload: {
        from?: string;
        to?: string;
        search?: string;
        mealChoices?: string[];
        schoolId?: number;
        educationLevelId?: number;
        selectedStudentId?: number;
        paymentStatus?: PaymentStatusEnum[];
    }): Observable<IBookedMealsDTOIn> {
        let params: HttpParams = new HttpParams();
        if (payload.from && payload.to) {
            params = params.set('from', payload.from);
            params = params.set('to', payload.to);
        }
        if (payload.educationLevelId) {
            params = params.set('education_level_id', payload.educationLevelId);
        }
        if (payload.search) {
            params = params.set('search', payload.search);
        }
        if (payload.mealChoices) {
            params = params.set('meal_choices', payload.mealChoices.join(','));
        }
        if (payload.selectedStudentId) {
            params = params.set('viewing_student_id', payload.selectedStudentId);
        }
        // si y a les deux status (payé et non payé) pas besoin de préciser)
        if (payload.paymentStatus?.length === 1) {
            params = params.set('paid', payload.paymentStatus[0] === PaymentStatusEnum.PAID ? 1 : 0);
        }
        return this.http.get<IBookedMealsDTOIn>(environment.wasoApi + SubUrlsEnum.BOOK_MEALS, { params });
    }

    public cancelMeal(idsToCancelToCancel: IBookedMealsToCancel): Observable<void> {
        const options: { headers: HttpHeaders; body: any } = {
            headers: new HttpHeaders(),
            body: idsToCancelToCancel,
        };
        return this.http.delete<void>(environment.wasoApi + SubUrlsEnum.CANCEL_BOOK_MEALS, options);
    }

    public postMealMenuFile(schoolId: number, date: Date, file: any): Observable<any> {
        const httpOptions: any = {
            headers: new HttpHeaders({
                ['Content-Type']: 'multipart/form-data',
            }),
        };
        const formData: FormData = new FormData();

        const dateString: string = date.getFullYear() + '-' + (date.getMonth() + 1) + '-' + date.getDate();

        formData.append('meal_file[date]', dateString);
        formData.append('meal_file[file]', file);

        return this.http.post<any>(
            environment.wasoApi + SubUrlsEnum.MEAL_FILE.replace('{{schoolId}}', schoolId.toString()),
            formData,
            httpOptions
        );
    }

    public deleteMealMenuFile(schoolId: number, date: Date): Observable<void> {
        const dateString: string = date.getFullYear() + '-' + (date.getMonth() + 1) + '-' + date.getDate();
        const params: HttpParams = new HttpParams().set('date', dateString);
        return this.http.delete<void>(environment.wasoApi + SubUrlsEnum.MEAL_FILE.replace('{{schoolId}}', schoolId.toString()), { params });
    }

    public getMealMenuDatas(schoolId: number): Observable<IMealMenuFiles> {
        return this.http.get<IMealMenuFiles>(environment.wasoApi + SubUrlsEnum.MEAL_FILE.replace('{{schoolId}}', schoolId.toString()));
    }
}
